import { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Helmet } from "react-helmet";
import { Stack } from "@mui/material";
import { useFlags } from "launchdarkly-react-client-sdk";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

import TimeoffLayout from "components/Screens/Timeoff/Layout";
import LeaveTypeCard from "components/Screens/Timeoff/ManageTimeoff/LeaveTypeCard";
import ToolBar from "components/Widgets/PageToolBarV2";
import PendingLeaves from "components/Screens/Timeoff/ManageTimeoff/PendingLeaves";
import UpcomingLeavesAndHolidaysBar from "components/Screens/Timeoff/ManageTimeoff/UpcomingLeavesAndHolidaysBar";
import ScheduledLeaves from "components/Screens/Timeoff/ManageTimeoff/ScheduledLeaves";
import CancelledLeaves from "components/Screens/Timeoff/ManageTimeoff/CancelledLeaves";
import RejectedLeaves from "components/Screens/Timeoff/ManageTimeoff/RejectedLeaves";
import TakenLeaves from "components/Screens/Timeoff/ManageTimeoff/TakenLeaves";
import PageLoader from "components/common/PageLoader";
import LeavesValidityDates from "components/Screens/Timeoff/TimeoffAdmin/EmployeeTimeOffDetails/LeavesValidityDates";

import { FetchLeaveTypes } from "store/actions/Timeoff/LeaveTypes";
import { FetchLeaveRequests } from "store/actions/Timeoff/LeaveRequests";
import { FetchUpcomingLeaves } from "store/actions/Timeoff/UpcomingLeaves";

import { TIMEOFF_REQUESTS } from "constants";

import "components/Screens/Timeoff/ManageTimeoff/ManageTimeoff.scss";

function ManageTimeoff() {
  const dispatch = useDispatch();
  const { anniversaryBasedTimeOff, futureCycleLeavesFiling } = useFlags();
  const scrollContainerRef = useRef(null);

  const [currentTab, setCurrentTab] = useState(TIMEOFF_REQUESTS[0].title);
  const [tabs, setTabs] = useState(TIMEOFF_REQUESTS);
  const [totalLeavesAndHolidaysCount, setTotalLeavesAndHolidaysCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const leaveTypes = useSelector((state) => state?.TimeoffLeaveTypes?.leaveTypes);
  const loadingLeaveTypes = useSelector((state) => state?.TimeoffLeaveTypes?.loadingLeaveTypes);

  const leaveRequests = useSelector((state) => state?.TimeoffLeaveRequests?.leaveRequests);
  const loadingLeavesRequest = useSelector((state) => state?.TimeoffLeaveTypes?.loadingLeavesRequest);

  const fetchLeavesCount = () => {
    const allTabsCount = TIMEOFF_REQUESTS.map((activity) => ({
      ...activity,
      count: leaveRequests?.[activity?.title?.toLowerCase()]?.length,
    }));
    setTabs(allTabsCount || TIMEOFF_REQUESTS);
  };

  const checkScrollButtons = () => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: -220,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({
        left: 220,
        behavior: "smooth",
      });
    }
  };

  const renderLeaveTypeCards = () => {
    const cards = [];

    if (leaveTypes.length === 0) return null;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < leaveTypes?.length; i++) {
      const leave = leaveTypes[i];
      if (anniversaryBasedTimeOff || leave?.visible) {
        cards.push(<LeaveTypeCard key={i} leave={leave} />);
      }
    }

    return (
      <div className="leave-cards-scroll-wrapper">
        <div
          className={`scroll-arrow scroll-arrow-left ${!canScrollLeft ? "disabled" : ""}`}
          onClick={scrollLeft}
          disabled={!canScrollLeft}
        >
          <ChevronLeftIcon />
        </div>

        <div className="leave-type-cards-container scrollable" ref={scrollContainerRef} onScroll={checkScrollButtons}>
          {cards}
        </div>

        <div
          className={`scroll-arrow scroll-arrow-right ${!canScrollRight ? "disabled" : ""}`}
          onClick={scrollRight}
          disabled={!canScrollRight}
        >
          <ChevronRightIcon />
        </div>
      </div>
    );
  };

  const renderToolBar = () => {
    return <ToolBar tabs={tabs} currentTab={currentTab} setCurrentTab={setCurrentTab} />;
  };

  const renderContent = () => {
    switch (currentTab) {
      case "Pending":
        return <PendingLeaves />;
      case "Scheduled":
        return <ScheduledLeaves />;
      case "Rejected":
        return <RejectedLeaves />;
      case "Cancelled":
        return <CancelledLeaves />;
      case "Taken":
        return <TakenLeaves />;
      default:
        return <PendingLeaves />;
    }
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);
        await dispatch(FetchLeaveRequests({ anniversaryBasedTimeOff }));
        await dispatch(FetchLeaveTypes({ flag: anniversaryBasedTimeOff, futureCycleFlag: futureCycleLeavesFiling }));
        await dispatch(FetchUpcomingLeaves({ flag: anniversaryBasedTimeOff }));
      } catch (error) {
        console.error("Error loading timeoff data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAllData();
  }, [dispatch, anniversaryBasedTimeOff]);

  useEffect(() => {
    fetchLeavesCount();
  }, [leaveRequests]);

  useEffect(() => {
    const timer = setTimeout(() => {
      checkScrollButtons();
    }, 100);
    return () => clearTimeout(timer);
  }, [leaveTypes]);

  useEffect(() => {
    const handleResize = () => {
      checkScrollButtons();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  return (
    <>
      <Helmet>
        <title>Edge | Timeoff</title>
      </Helmet>
      {(loadingLeaveTypes || loadingLeavesRequest || loading) && <PageLoader />}
      <div className="manage-timeoff-wrapper">
        <TimeoffLayout />
        <div className="manage-timeoff-container">
          <Stack direction="row" spacing="12px" alignItems="start" justifyContent="space-between">
            {!!leaveTypes.length && <div className="timeoff-subheading timeoff-subheading-margin">Leaves taken</div>}

            {leaveTypes?.length && leaveTypes?.[0]?.startDate && leaveTypes?.[0]?.endDate ? (
              <LeavesValidityDates startDate={leaveTypes?.[0]?.startDate} endDate={leaveTypes?.[0]?.endDate} />
            ) : null}
          </Stack>

          {renderLeaveTypeCards()}

          <div className="timeoff-subheading upcoming-leaves-margin">
            Upcoming leaves & holidays ({totalLeavesAndHolidaysCount})
          </div>
          <UpcomingLeavesAndHolidaysBar setTotalLeavesAndHolidaysCount={setTotalLeavesAndHolidaysCount} />

          <div className="timeoff-subheading upcoming-leaves-margin">Your time-off requests</div>

          {renderToolBar()}

          {renderContent()}
        </div>
      </div>
    </>
  );
}

export default ManageTimeoff;
